import React from 'react';
import ReactDOM from 'react-dom';
import {ST} from "./SystemText";
import {isCordova, isStandalone} from "./AppMode";

// Spoločné skenovanie QR / EAN pre MobileHomePage, vyhľadávanie a košík.
// Cordova: natívny barcodeScanner plugin (rovnaké nastavenia ako predtým).
// PWA / prehliadač: kamera cez getUserMedia + @zxing/browser (components/CameraScanner.js).
// scanCode() vracia Promise s textom kódu; pri zrušení alebo chybe sa resolve-ne s null
// (chyby už zobrazí sám).

export function showAlert(msg, title = 'Info') {
    if (window.navigator && window.navigator.notification && typeof window.navigator.notification.alert === 'function') {
        window.navigator.notification.alert(msg, () => {}, title, 'OK');
    } else {
        window.alert((title ? title + ': ' : '') + msg);
    }
}

function cordovaScanner() {
    return window.cordova && window.cordova.plugins ? window.cordova.plugins.barcodeScanner : undefined;
}

function hasCamera() {
    return !!(window.navigator.mediaDevices && window.navigator.mediaDevices.getUserMedia);
}

// tlačidlá skenera: Cordova s pluginom, alebo nainštalovaná PWA s kamerou.
// Na bežnom webe v prehliadači ostávajú skryté ako pred zavedením PWA.
export function canScan() {
    if (isCordova()) {
        return !!cordovaScanner();
    }
    return isStandalone() && hasCamera();
}

function scanWithCordova(bs, resolve) {
    const startScan = () => {
        // vyber si formáty (stačí QR_CODE; môžeš pridať EAN_13, CODE_128 podľa potreby)
        const options = {
            formats: 'QR_CODE,EAN_13,CODE_128',
            preferFrontCamera: false,
            showFlipCameraButton: true,
            showTorchButton: true,
            torchOn: false,
            saveHistory: false,
            prompt: 'Place a barcode inside the scan area',
            resultDisplayDuration: 0,
            disableAnimations: true,
            disableSuccessBeep: false
        };

        bs.scan(
            (result) => {
                const code = (result && result.text) ? String(result.text).trim() : '';
                if (code) {
                    resolve(code);
                } else {
                    showAlert('Nenašiel sa žiadny kód.', 'Info');
                    resolve(null);
                }
            },
            (error) => {
                showAlert('Scanning failed: ' + error, 'Error');
                resolve(null);
            },
            options
        );
    };

    // (voliteľné) požiadanie o povolenie – väčšina verzií si ho vypýta sama pri scan()
    if (typeof bs.requestPermission === 'function') {
        bs.requestPermission(
            () => startScan(),
            (err) => {
                showAlert('Povolenie kamery bolo zamietnuté: ' + err, 'Povolenie');
                resolve(null);
            }
        );
    } else {
        startScan();
    }
}

// CameraScanner (@zxing) sa načíta až pri prvom skenovaní ako samostatný chunk – bežný web ho nesťahuje
function scanWithCamera(resolve) {
    import("../components/CameraScanner").then(({default: CameraScanner}) => {
        let container = document.createElement("div");
        document.body.appendChild(container);
        const close = (value) => {
            ReactDOM.unmountComponentAtNode(container);
            container.remove();
            resolve(value);
        };
        ReactDOM.render(
            <CameraScanner
                prompt={ST.getOr("scan_code", "portal", "Namierte kameru na QR alebo čiarový kód")}
                cancelLabel={ST.getOr("cancel", "global", "Zrušiť")}
                onResult={(code) => close(code)}
                onCancel={() => close(null)}
                onError={(error) => {
                    if (error && (error.name === "NotAllowedError" || error.name === "SecurityError")) {
                        showAlert('Povolenie kamery bolo zamietnuté.', 'Povolenie');
                    } else if (error && error.name === "NotFoundError") {
                        showAlert('Zariadenie nemá dostupnú kameru.', 'Info');
                    } else {
                        showAlert('Scanning failed: ' + (error && error.message ? error.message : error), 'Error');
                    }
                    close(null);
                }}
            />,
            container
        );
    }).catch((error) => {
        // napr. offline pred prvým skenovaním (chunk ešte nie je v cache)
        showAlert('Skener sa nepodarilo načítať: ' + (error && error.message ? error.message : error), 'Error');
        resolve(null);
    });
}

export function scanCode() {
    return new Promise((resolve) => {
        if (window.cordova) {
            let bs = cordovaScanner();
            if (!bs) {
                showAlert('Skener nie je dostupný. Skontroluj, či je plugin nainštalovaný.', 'Chýba plugin');
                resolve(null);
                return;
            }
            scanWithCordova(bs, resolve);
            return;
        }
        if (!hasCamera()) {
            showAlert('Skenovanie je dostupné len na zariadení s kamerou.', 'Info');
            resolve(null);
            return;
        }
        scanWithCamera(resolve);
    });
}
